import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import PortalStatusPill from './PortalStatusPill';
import { portalCardSx } from './portalStyles';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import DrawIcon from '@mui/icons-material/Draw';
import PortalQuoteService from '../../services/PortalQuoteService';
import { useToast } from '../../contexts/ToastContext';

const STATUS_CHIP = {
    SENT:                        { label: 'Sent',               color: '#4A90E2' },
    AWAITING_CUSTOMER_SIGNATURE: { label: 'Awaiting Signature', color: '#F4A261' },
    AWAITING_SIGNATURE:          { label: 'Awaiting Signature', color: '#F4A261' },
    CHANGES_REQUESTED:           { label: 'Changes Requested',  color: '#F4C067' },
    SIGNED:                      { label: 'Signed',             color: '#2EC4B6' },
    ACCEPTED:                    { label: 'Accepted',           color: '#7DCFB6' },
    REJECTED:                    { label: 'Declined',           color: '#E07A5F' },
    EXPIRED:                     { label: 'Expired',            color: '#B0B0B0' },
    CANCELLED:                   { label: 'Cancelled',          color: '#B0B0B0' },
};

const statusChip = (status) => STATUS_CHIP[status] || { label: status, color: '#9E9E9E' };
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
const fmtAmount = (v) => v != null ? `CHF ${Number(v).toFixed(2)}` : '—';

const SideLabel = ({ children }) => (
    <Typography sx={{ fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, mb: 0.4 }}>
        {children}
    </Typography>
);

const SideValue = ({ children, sx }) => (
    <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#1E293B', ...sx }}>
        {children}
    </Typography>
);

export default function PortalQuoteDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [loading, setLoading] = useState(true);
    const [quote, setQuote] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);

    const [acceptOpen, setAcceptOpen] = useState(false);
    const [declineOpen, setDeclineOpen] = useState(false);
    const [changesOpen, setChangesOpen] = useState(false);
    const [changesComment, setChangesComment] = useState('');

    const loadQuote = async () => {
        setLoading(true);
        try {
            const data = await PortalQuoteService.getQuote(id);
            setQuote(data);
        } catch {
            showToast('Failed to load quote', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadQuote();
        let url;
        PortalQuoteService.downloadPdf(id)
            .then(response => {
                url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                setPdfUrl(url);
            })
            .catch(() => {});
        return () => { if (url) URL.revokeObjectURL(url); };
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDownloadPdf = async () => {
        try {
            const response = await PortalQuoteService.downloadPdf(id);
            const filename = `${quote?.displayName || 'quote'}.pdf`;
            const blobUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(blobUrl);
        } catch {
            showToast('Failed to download PDF', 'error');
        }
    };

    const handleAccept = async () => {
        setSubmitting(true);
        try {
            await PortalQuoteService.accept(id);
            showToast('Quote accepted successfully', 'success');
            setAcceptOpen(false);
            await loadQuote();
        } catch {
            showToast('Failed to accept quote', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDecline = async () => {
        setSubmitting(true);
        try {
            await PortalQuoteService.decline(id);
            showToast('Quote declined', 'info');
            setDeclineOpen(false);
            await loadQuote();
        } catch {
            showToast('Failed to decline quote', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRequestChanges = async () => {
        if (!changesComment.trim()) return;
        setSubmitting(true);
        try {
            await PortalQuoteService.requestChanges(id, changesComment);
            showToast('Change request submitted', 'success');
            setChangesOpen(false);
            setChangesComment('');
            await loadQuote();
        } catch {
            showToast('Failed to submit change request', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <PortalLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                    <CircularProgress sx={{ color: '#427898' }} />
                </Box>
            </PortalLayout>
        );
    }

    if (!quote) {
        return (
            <PortalLayout>
                <Box sx={{ p: 4 }}>
                    <Typography>Quote not found.</Typography>
                    <Button onClick={() => navigate('/portal/quotes')} sx={{ mt: 2 }}>Back to Quotes</Button>
                </Box>
            </PortalLayout>
        );
    }

    const chip = statusChip(quote.status);
    const isSent = quote.status === 'SENT';
    const isAwaitingSignature = quote.status === 'AWAITING_CUSTOMER_SIGNATURE' || quote.status === 'AWAITING_SIGNATURE';
    const isChangesRequested = quote.status === 'CHANGES_REQUESTED';
    const isReadOnly = ['SIGNED', 'ACCEPTED', 'REJECTED', 'EXPIRED', 'CANCELLED'].includes(quote.status);
    const isExpired = new Date(quote.validUntil) < new Date();

    return (
        <PortalLayout>
            <Box>

                {/* ── Sticky header ── */}
                <Box sx={{
                    ...portalCardSx,
                    px: 2.5, py: 1.25, mb: 3,
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    position: 'sticky', top: 12, zIndex: 10,
                }}>
                    <Button
                        size="small"
                        startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={() => navigate('/portal/quotes')}
                        sx={{ color: '#64748B', fontWeight: 500, textTransform: 'none', fontSize: 13, px: 1, minWidth: 0, '&:hover': { bgcolor: '#F1F5F9' } }}
                    >
                        Quotes
                    </Button>
                    <Divider orientation="vertical" flexItem sx={{ height: 16, alignSelf: 'center' }} />
                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0F172A', whiteSpace: 'nowrap' }}>
                        {quote.displayName}
                    </Typography>
                    <PortalStatusPill status={quote.status} label={chip.label} />
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title="Download PDF">
                        <IconButton size="small" onClick={handleDownloadPdf}
                            sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                            <DownloadIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* ── Body ── */}
                <Box sx={{
                    display: 'flex', gap: 3, alignItems: 'flex-start',
                    flexDirection: { xs: 'column', md: 'row' },
                }}>

                    {/* ── Main / left column ── */}
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

                        {/* SENT: accept / request changes / decline */}
                        {isSent && (
                            <Paper elevation={0} sx={{ ...portalCardSx, p: 3 }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
                                    Your Response
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                                    Please review this quote and let us know your decision.
                                </Typography>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                                    <Button
                                        variant="contained"
                                        onClick={() => setAcceptOpen(true)}
                                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', bgcolor: '#2EC4B6', '&:hover': { bgcolor: '#25a89b', boxShadow: 'none' } }}
                                    >
                                        Accept Quote
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setChangesOpen(true)}
                                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, borderColor: '#CBD5E1', color: '#475569', '&:hover': { borderColor: '#427898', color: '#427898', bgcolor: '#F0F7FF' } }}
                                    >
                                        Request Changes
                                    </Button>
                                    <Box sx={{ flex: 1 }} />
                                    <Button
                                        onClick={() => setDeclineOpen(true)}
                                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, color: '#EF4444', '&:hover': { bgcolor: '#FEF2F2' } }}
                                    >
                                        Decline
                                    </Button>
                                </Stack>
                            </Paper>
                        )}

                        {/* AWAITING_SIGNATURE */}
                        {isAwaitingSignature && (
                            <Paper elevation={0} sx={{ ...portalCardSx, border: '1.5px solid #427898', bgcolor: '#EFF6FF', p: 3 }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#427898', textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
                                    Signature Required
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                                    Your signature is required to finalise this quote. Click below to open the signing document.
                                </Typography>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                                    {quote.customerSigningUrl && (
                                        <Button
                                            variant="contained"
                                            startIcon={<DrawIcon />}
                                            onClick={() => window.open(quote.customerSigningUrl, '_blank', 'noopener,noreferrer')}
                                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', bgcolor: '#427898', '&:hover': { bgcolor: '#355f77', boxShadow: 'none' } }}
                                        >
                                            Sign Document
                                        </Button>
                                    )}
                                    <Button
                                        variant="outlined"
                                        onClick={() => setChangesOpen(true)}
                                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, borderColor: '#CBD5E1', color: '#475569', '&:hover': { borderColor: '#427898', color: '#427898', bgcolor: '#F0F7FF' } }}
                                    >
                                        Request Changes
                                    </Button>
                                </Stack>
                            </Paper>
                        )}

                        {/* CHANGES_REQUESTED */}
                        {isChangesRequested && (
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                Your change request is being reviewed. We will get back to you shortly.
                            </Alert>
                        )}

                        {/* SIGNED / ACCEPTED */}
                        {(quote.status === 'SIGNED' || quote.status === 'ACCEPTED') && (
                            <Alert severity="success" sx={{ borderRadius: 2 }}>
                                {quote.status === 'SIGNED'
                                    ? 'This quote has been signed and is now valid.'
                                    : 'This quote has been accepted.'}
                            </Alert>
                        )}

                        {/* REJECTED / EXPIRED / CANCELLED */}
                        {isReadOnly && !['SIGNED', 'ACCEPTED'].includes(quote.status) && (
                            <Alert severity={quote.status === 'REJECTED' ? 'error' : 'warning'} sx={{ borderRadius: 2 }}>
                                {quote.status === 'REJECTED' && 'This quote has been declined.'}
                                {quote.status === 'EXPIRED' && 'This quote has expired.'}
                                {quote.status === 'CANCELLED' && 'This quote has been cancelled.'}
                            </Alert>
                        )}

                        {/* PDF */}
                        <Paper elevation={0} sx={portalCardSx}>
                            {pdfUrl ? (
                                <iframe
                                    src={pdfUrl}
                                    title="Quote PDF"
                                    width="100%"
                                    height="800px"
                                    style={{ border: 'none', display: 'block' }}
                                />
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                                    <CircularProgress size={24} sx={{ color: '#CBD5E1' }} />
                                    <Typography sx={{ color: '#94A3B8', fontSize: 13, mt: 1.5 }}>Loading document…</Typography>
                                </Box>
                            )}
                        </Paper>
                    </Box>

                    {/* ── Right sidebar ── */}
                    <Box sx={{ width: { md: 272 }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2, position: { md: 'sticky' }, top: { md: 72 } }}>

                        {/* Details */}
                        <Paper elevation={0} sx={{ ...portalCardSx, p: 2.5 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
                                Details
                            </Typography>
                            <Stack spacing={2}>
                                {quote.customerName && (
                                    <Box>
                                        <SideLabel>Company</SideLabel>
                                        <SideValue>{quote.customerName}</SideValue>
                                    </Box>
                                )}
                                {quote.customerContactName && (
                                    <Box>
                                        <SideLabel>Contact</SideLabel>
                                        <SideValue>{quote.customerContactName}</SideValue>
                                    </Box>
                                )}
                                <Box>
                                    <SideLabel>Issued</SideLabel>
                                    <SideValue>{fmtDate(quote.quoteDate)}</SideValue>
                                </Box>
                                {quote.validUntil && (
                                    <Box>
                                        <SideLabel>Valid Until</SideLabel>
                                        <SideValue sx={{ color: isExpired ? '#EF4444' : '#1E293B' }}>
                                            {fmtDate(quote.validUntil)}
                                        </SideValue>
                                    </Box>
                                )}
                            </Stack>
                        </Paper>

                        {/* Financials */}
                        <Paper elevation={0} sx={{ ...portalCardSx, p: 2.5 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
                                Summary
                            </Typography>
                            <Stack spacing={1}>
                                {quote.totalBeforeTax != null && (
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>Subtotal</Typography>
                                        <Typography sx={{ fontSize: 13, color: '#475569' }}>{fmtAmount(quote.totalBeforeTax)}</Typography>
                                    </Stack>
                                )}
                                {quote.taxAmount != null && (
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>Tax ({quote.taxRate ?? 0}%)</Typography>
                                        <Typography sx={{ fontSize: 13, color: '#475569' }}>{fmtAmount(quote.taxAmount)}</Typography>
                                    </Stack>
                                )}
                                <Divider sx={{ my: 0.5 }} />
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0F172A' }}>Total</Typography>
                                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#427898' }}>{fmtAmount(quote.totalAmount)}</Typography>
                                </Stack>
                            </Stack>
                        </Paper>
                    </Box>
                </Box>

                {/* ── Dialogs ── */}

                <Dialog open={acceptOpen} onClose={() => setAcceptOpen(false)} maxWidth="xs" fullWidth
                    PaperProps={{ sx: { borderRadius: 2 } }}>
                    <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>Accept Quote</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to accept this quote? This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2.5 }}>
                        <Button onClick={() => setAcceptOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={handleAccept}
                            disabled={submitting}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', bgcolor: '#2EC4B6', '&:hover': { bgcolor: '#25a89b', boxShadow: 'none' } }}
                        >
                            {submitting ? 'Accepting…' : 'Accept Quote'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={declineOpen} onClose={() => setDeclineOpen(false)} maxWidth="xs" fullWidth
                    PaperProps={{ sx: { borderRadius: 2 } }}>
                    <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>Decline Quote</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to decline this quote?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2.5 }}>
                        <Button onClick={() => setDeclineOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleDecline}
                            disabled={submitting}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none' }}
                        >
                            {submitting ? 'Declining…' : 'Decline Quote'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={changesOpen} onClose={() => setChangesOpen(false)} maxWidth="sm" fullWidth
                    PaperProps={{ sx: { borderRadius: 2 } }}>
                    <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>Request Changes</DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ mb: 2 }}>
                            Describe the changes you would like to request:
                        </DialogContentText>
                        <TextField
                            autoFocus
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={8}
                            label="Your comment"
                            value={changesComment}
                            onChange={(e) => setChangesComment(e.target.value)}
                            variant="outlined"
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2.5 }}>
                        <Button onClick={() => { setChangesOpen(false); setChangesComment(''); }} sx={{ textTransform: 'none' }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleRequestChanges}
                            disabled={submitting || !changesComment.trim()}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', bgcolor: '#427898', '&:hover': { bgcolor: '#355f77', boxShadow: 'none' } }}
                        >
                            {submitting ? 'Submitting…' : 'Submit Request'}
                        </Button>
                    </DialogActions>
                </Dialog>

            </Box>
        </PortalLayout>
    );
}
